import { Api } from 'src/common';
import { AccountType } from 'src/features/common/types/bank-details-validation';
import { IdType, ValidationResult } from './credit-vetting.service';
import { parseObjectToQueryParamString } from 'src/helpers/functional-helpers';
import { Environment } from 'src/Environment';

const AccountTypeMap = {
  CURRENT_CHEQUE: '1-Current',
  SAVINGS: '2-Savings',
  TRANSMISSION: '3-Transmission',
};

enum CDVStatus {
  INVALID = '0',
  VALID = '1',
}

type CDVCheckResponse = {
  code: CDVStatus;
  message: string;
  description?: string;
};

const cdvCheck = async (
  branchCode: string,
  bankAccountNumber: string,
  accountType: AccountType | string,
  abortController: AbortController,
): Promise<boolean> => {
  const _accountType = AccountTypeMap[accountType] ?? accountType;

  const query = `vetting/manage/cdv?branchCode=${branchCode}&bankAccountNumber=${bankAccountNumber}&accountType=${_accountType}`;

  const { response, status } = await Api.getV2<CDVCheckResponse>(query, {
    signal: abortController.signal,
  });

  if (status !== 200) {
    throw new Error(
      `CDV: error response from CDV ${query} GET endpoint: response code: ${status}, response: ${JSON.stringify(
        response,
      )}`,
    );
  }

  return response.code === CDVStatus.VALID;
};

type BAVCheckResponse = {
  code: number;
  result: ValidationResult;
  successful: boolean;
};

type BAVCheckResult = {
  ok: boolean;
  message?: string;
};

//  Credit Vetting service
const bavCheck = async (
  accountNumber: string,
  accountType: AccountType | string,
  bankName: string,
  branchCode: string,
  idCompRegNo: string,
  surnameCompName: string,
  idType: IdType,
  abortController: AbortController,
): Promise<BAVCheckResult> => {
  const endpoint = '/cloud/api/vetting-manage/validateBankAccount';

  const params = {
    accountNumber: accountNumber,
    accountType: AccountTypeMap[accountType] ?? accountType,
    bankName: bankName,
    branchCode: branchCode,
    idCompRegNo: idCompRegNo,
    idType: idType,
    surnameCompName: surnameCompName,
  };
  const path = `${endpoint}?${parseObjectToQueryParamString(params)}`;
  const url = encodeURI(Environment.current?.apiHostCloud + path);

  try {
    const { response, status } = await Api.getV2<BAVCheckResponse>(url, {
      signal: abortController.signal,
    });

    if (status > 300 && status < 500) {
      throw new Error(
        "Uh oh! We're experiencing technical difficulties, but we're working on it. Please try again later.",
      );
    }

    if (status >= 500 && !abortController?.signal?.aborted) {
      throw new Error(
        `BAV: error response from BAV ${endpoint} GET endpoint: response: ${JSON.stringify(
          response,
        )} code: ${status}`,
      );
    }

    return { ok: response.result === ValidationResult.VALID };
  } catch (error) {
    console.error(error);
    return {
      ok: false,
      message:
        "Uh oh! We're experiencing technical difficulties, but we're working on it. Please try again later.",
    };
  }
};

export const BankValidationService = {
  cdvCheck,
  bavCheck,
};
